/**
 * Dashboard Data Aggregation Hook
 * 
 * Fetches and aggregates comprehensive dashboard metrics for clients and vendors.
 * This hook performs complex multi-table queries to provide a complete overview
 * of job statuses, financial metrics, and upcoming milestones.
 * 
 * **Data Fetched:**
 * - Job counts by status (draft, rfq_open, awarded, in_progress, etc.)
 * - Total value of jobs in progress
 * - Total value of completed jobs
 * - Upcoming milestones (next 5, sorted by due date)
 * - Job details for each milestone
 * 
 * **Query Strategy:**
 * The hook uses a multi-step query approach to optimize performance:
 * 1. Fetch jobs for user's organization
 * 2. Fetch awards for those jobs
 * 3. Fetch award selections to calculate totals
 * 4. Fetch upcoming milestones with date filtering
 * 5. Fetch job details for milestones
 * 6. Enrich and combine all data
 * 
 * **Financial Calculations:**
 * - Value in Progress: Sum of awarded, in_progress, and completed_pending_review jobs
 * - Value Completed: Sum of accepted and closed jobs
 * - All values stored in cents, displayed as dollars
 * 
 * @module useDashboardData
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';

/**
 * Job counts organized by status
 * Each property represents a job status and its count
 */
interface JobCountsByStatus {
  draft: number;
  rfq_open: number;
  awarded: number;
  in_progress: number;
  completed_pending_review: number;
  accepted: number;
  rejected: number;
  disputed: number;
  closed: number;
}

/**
 * Milestone with enriched job information for dashboard display
 */
interface MilestoneWithJob {
  /** Milestone unique ID */
  id: string;
  /** Milestone name/title */
  name: string;
  /** Due date in ISO format */
  due_date: string;
  /** Whether milestone has been marked complete */
  is_completed: boolean;
  /** Who is responsible for this milestone */
  action_owner: 'client' | 'vendor';
  /** Associated job ID */
  job_id: string;
  /** Associated job title */
  job_title: string;
  /** Associated job public ID (for routing) */
  job_public_id: string;
  /** Associated job status */
  job_status: string;
}

/**
 * Complete dashboard data structure
 */
interface DashboardData {
  /** Job counts organized by status */
  jobCountsByStatus: JobCountsByStatus;
  /** Array of next 5 upcoming milestones */
  upcomingMilestones: MilestoneWithJob[];
  /** Total value (in cents) of jobs in progress */
  valueInProgress: number;
  /** Total value (in cents) of completed jobs */
  valueCompleted: number;
  /** Currency code (typically 'USD') */
  currency: string;
  /** Loading state for data fetching */
  loading: boolean;
  /** Error message if any query fails */
  error: string | null;
}

/**
 * Dashboard Data Hook
 * 
 * Fetches comprehensive dashboard metrics for the current user's organization.
 * Uses React Query for caching and automatic refetching.
 * 
 * The hook performs two parallel queries:
 * 1. Job counts and financial metrics
 * 2. Upcoming milestones
 * 
 * Both queries are organization-scoped and only execute when user is authenticated.
 * 
 * @returns {DashboardData} Complete dashboard data with loading and error states
 * 
 * @example
 * ```tsx
 * function Dashboard() {
 *   const {
 *     jobCountsByStatus,
 *     upcomingMilestones,
 *     valueInProgress,
 *     valueCompleted,
 *     currency,
 *     loading,
 *     error
 *   } = useDashboardData();
 *   
 *   if (loading) return <Spinner />;
 *   if (error) return <Error message={error} />;
 *   
 *   return (
 *     <div>
 *       <h2>Jobs Overview</h2>
 *       <MetricsGrid counts={jobCountsByStatus} />
 *       <FinancialSummary
 *         inProgress={valueInProgress / 100}
 *         completed={valueCompleted / 100}
 *         currency={currency}
 *       />
 *       <UpcomingMilestones milestones={upcomingMilestones} />
 *     </div>
 *   );
 * }
 * ```
 */
export const useDashboardData = (): DashboardData => {
  const { user } = useAuth();

  const { data: jobCounts, isLoading: jobCountsLoading, error: jobCountsError } = useQuery({
    queryKey: ['dashboard-job-counts', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      const defaultCounts: JobCountsByStatus = {
        draft: 0,
        rfq_open: 0,
        awarded: 0,
        in_progress: 0,
        completed_pending_review: 0,
        accepted: 0,
        rejected: 0,
        disputed: 0,
        closed: 0,
      };

      if (!profile) {
        console.warn('[useDashboardData] Profile not found, returning defaults');
        return { counts: defaultCounts, valueInProgress: 0, valueCompleted: 0, currency: 'USD' };
      }

      // Step 1: Fetch jobs
      const { data: jobsData, error: jobsError } = await supabase
        .from('jobs')
        .select('id, status')
        .eq('organization_id', profile.organization_id)
        .eq('is_deleted', false);

      if (jobsError) {
        console.error('[useDashboardData] Jobs fetch error:', jobsError);
        return { counts: defaultCounts, valueInProgress: 0, valueCompleted: 0, currency: 'USD' };
      }

      const counts: JobCountsByStatus = {
        draft: 0,
        rfq_open: 0,
        awarded: 0,
        in_progress: 0,
        completed_pending_review: 0,
        accepted: 0,
        rejected: 0,
        disputed: 0,
        closed: 0,
      };

      let valueInProgress = 0;
      let valueCompleted = 0;
      let currencyValue = 'USD';

      if (!jobsData || jobsData.length === 0) {
        return { counts, valueInProgress, valueCompleted, currency: currencyValue };
      }

      // Step 2: Fetch awards for these jobs
      const jobIds = jobsData.map((j: any) => j.id);
      const { data: awardsData } = await supabase
        .from('awards')
        .select('id, job_id, winning_bid_id')
        .in('job_id', jobIds);

      // Fetch purchase orders to get adjusted values
      const { data: purchaseOrdersData } = await supabase
        .from('purchase_orders')
        .select('id, job_id, po_amount_cents')
        .in('job_id', jobIds);

      if (!awardsData || awardsData.length === 0) {
        jobsData.forEach((job: any) => {
          const status = job.status as keyof JobCountsByStatus;
          if (counts[status] !== undefined) {
            counts[status]++;
          }
        });
        return { counts, valueInProgress, valueCompleted, currency: currencyValue };
      }

      // Step 3: Fetch award selections for total amounts
      const winningBidIds = awardsData.map((a: any) => a.winning_bid_id);
      const { data: awardSelectionsData } = await supabase
        .from('award_selections')
        .select('award_id, total_price_cents')
        .in('award_id', awardsData.map((a: any) => a.id));

      // Build maps
      const awardByJobId = new Map(awardsData.map((a: any) => [a.job_id, a]));
      const selectionsByAwardId = new Map<string, number>();

      // Calculate total per award
      awardSelectionsData?.forEach(sel => {
        const current = selectionsByAwardId.get(sel.award_id) || 0;
        selectionsByAwardId.set(sel.award_id, current + sel.total_price_cents);
      });

      // Build PO map (PO values include approved adjustments)
      const poByJobId = new Map(
        (purchaseOrdersData || []).map((po: any) => [po.job_id, po])
      );

      // Set currency
      currencyValue = 'USD'; // Default currency

      // Calculate counts and values
      jobsData.forEach((job: any) => {
        const status = job.status as keyof JobCountsByStatus;
        if (counts[status] !== undefined) {
          counts[status]++;
        }

        // Get the job value: prioritize PO amount (includes adjustments), fall back to award amount
        let jobValue = 0;
        const po = poByJobId.get(job.id);
        
        if (po && po.po_amount_cents) {
          // Use PO amount if available (includes approved adjustments)
          jobValue = po.po_amount_cents;
        } else {
          // Fall back to award selections total if no PO yet
          const award = awardByJobId.get(job.id);
          jobValue = award ? (selectionsByAwardId.get(award.id) || 0) : 0;
        }

        // Calculate value in progress (awarded, in_progress, completed_pending_review)
        if (['awarded', 'in_progress', 'completed_pending_review'].includes(status)) {
          valueInProgress += jobValue;
        }

        // Calculate value completed (accepted, closed)
        if (['accepted', 'closed'].includes(status)) {
          valueCompleted += jobValue;
        }
      });

      return { counts, valueInProgress, valueCompleted, currency: currencyValue };
    },
    enabled: !!user,
  });

  const { data: milestones, isLoading: milestonesLoading, error: milestonesError } = useQuery({
    queryKey: ['dashboard-upcoming-milestones', user?.id],
    queryFn: async () => {
      if (!user) throw new Error('User not authenticated');

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', user.id)
        .single();

      if (!profile) {
        console.warn('[useDashboardData] Profile not found for milestones query');
        return [] as MilestoneWithJob[];
      }

      const todayIso = new Date().toISOString();

      const { data: milestoneRows, error: milestonesFetchError } = await supabase
        .from('milestones')
        .select('*')
        .eq('organization_id', profile.organization_id)
        .eq('is_blank', false)
        .eq('is_completed', false)
        .gte('due_date', todayIso)
        .order('due_date', { ascending: true })
        .limit(5);

      if (milestonesFetchError) throw milestonesFetchError;

      const jobIds = Array.from(new Set((milestoneRows || []).map((m: any) => m.job_id).filter(Boolean)));
      if (jobIds.length === 0) return [] as MilestoneWithJob[];

      const { data: jobRows, error: jobsFetchError } = await supabase
        .from('jobs')
        .select('id, title, public_id, status, organization_id, is_deleted')
        .in('id', jobIds)
        .eq('organization_id', profile.organization_id)
        .eq('is_deleted', false);

      if (jobsFetchError) throw jobsFetchError;

      const jobsById = new Map((jobRows || []).map((j: any) => [j.id, j]));

      const enriched = (milestoneRows || [])
        .map((m: any) => {
          const j = jobsById.get(m.job_id);
          if (!j) return null;
          return {
            id: m.id,
            name: (m as any).milestone_name ?? (m as any).name ?? 'Milestone',
            due_date: m.due_date,
            is_completed: m.is_completed,
            action_owner: m.action_owner,
            job_id: m.job_id,
            job_title: j.title,
            job_public_id: j.public_id,
            job_status: j.status,
          } as MilestoneWithJob;
        })
        .filter(Boolean)
        .slice(0, 5) as MilestoneWithJob[];

      return enriched;
    },
    enabled: !!user,
  });

  return {
    jobCountsByStatus: jobCounts?.counts || {
      draft: 0,
      rfq_open: 0,
      awarded: 0,
      in_progress: 0,
      completed_pending_review: 0,
      accepted: 0,
      rejected: 0,
      disputed: 0,
      closed: 0,
    },
    valueInProgress: jobCounts?.valueInProgress || 0,
    valueCompleted: jobCounts?.valueCompleted || 0,
    currency: jobCounts?.currency || 'USD',
    upcomingMilestones: milestones || [],
    loading: jobCountsLoading || milestonesLoading,
    error: jobCountsError?.message || milestonesError?.message || null,
  };
};
